import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Interceptor per aggiungere il token alle richieste
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor per gestire errori di autenticazione
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  name: string;
  username: string;
  email: string;
  role: 'admin' | 'utente';
  can_edit_buoni?: boolean;
  can_delete_buoni?: boolean;
}

export interface LoginResponse {
  user: User;
  token: string;
  expires_at?: string;
  message: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ForgotPasswordOperatorsResponse {
  operators: Array<{ id: number; name: string }>;
  is_admin?: boolean;
}

export interface ResetPasswordRequest {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface Template {
  id: number;
  slug: string;
  name: string;
  title_data?: string | null;
  subtitle?: string | null;
  image_logo?: string | null;
  /** URL assoluto dell'immagine Lato Logo (restituito dal backend) */
  image_logo_url?: string | null;
  title?: string | null;
  phone?: string | null;
  address?: string | null;
  website?: string | null;
  text_color_components?: string | null;
  text_color_data?: string | null;
  bg_data_image?: string | null;
  bg_logo_image?: string | null;
  /** URL assoluto Sfondo Lato Dati (restituito dal backend) */
  bg_data_image_url?: string | null;
  /** URL assoluto Sfondo Lato Logo (restituito dal backend) */
  bg_logo_image_url?: string | null;
  /** Testo email invio buono: usa [a], [da], [regalo], [scadenza], [numero_buono] */
  email_body_template?: string | null;
  /** Oggetto email invio buono */
  email_subject?: string | null;
}

export const authService = {
  login: async (username: string, password: string, remember?: boolean): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/login', { username, password, remember: remember || false }, { timeout: 15000 });
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/logout');
  },

  me: async (): Promise<User> => {
    const response = await api.get<User>('/me', { timeout: 10000 });
    return response.data;
  },

  forgotPasswordOperators: async (username: string): Promise<ForgotPasswordOperatorsResponse> => {
    const response = await api.get<ForgotPasswordOperatorsResponse>('/forgot-password/operators', { params: { username } });
    return response.data;
  },

  forgotPassword: async (username: string, operatorId: number): Promise<ForgotPasswordResponse> => {
    const response = await api.post<ForgotPasswordResponse>('/forgot-password', { username, operator_id: operatorId });
    return response.data;
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/reset-password', data);
    return response.data;
  },
};

export const templateService = {
  getTemplates: async (): Promise<Template[]> => {
    const response = await api.get<Template[]>('/admin/templates');
    return response.data;
  },
  updateTemplate: async (id: number, data: Partial<Template>): Promise<{ message: string; template: Template }> => {
    const response = await api.put<{ message: string; template: Template }>(`/admin/templates/${id}`, data);
    return response.data;
  },
  /** Upload image for template (Immagine Lato Logo, etc.). Returns the stored filename to save in template. */
  uploadImage: async (file: File): Promise<{ filename: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ filename: string }>('/admin/templates/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export interface BuonoRow {
  id: number;
  slogan: string;
  a: string;
  da: string;
  regalo: string;
  mail: string;
  /** Telefono del cliente (del buono), non quello del template */
  telefono: string;
  operatore: string;
  creato: string;
  scade: string;
  stato: 'green' | 'red' | 'yellow';
}

/** Dettaglio buono per modifica e export PDF (GET /admin/buoni/:id). Se presente, template è popolato per la preview. */
export interface BuonoDetail {
  id: number;
  template_id: number;
  a: string;
  da: string;
  regalo: string;
  mail: string;
  telefono: string;
  operatore: string;
  scade_at: string; // YYYY-MM
  stato: 'green' | 'red' | 'yellow';
  template?: Template | null;
}

export const buonoService = {
  getBuoni: async (search?: string): Promise<BuonoRow[]> => {
    const params = search ? { search } : {};
    const response = await api.get<BuonoRow[]>('/admin/buoni', { params, timeout: 15000 });
    return response.data;
  },
  getBuono: async (id: number): Promise<BuonoDetail> => {
    const response = await api.get<BuonoDetail>(`/admin/buoni/${id}`);
    return response.data;
  },
  createBuono: async (data: {
    template_id: number;
    slogan?: string;
    a?: string;
    da?: string;
    regalo?: string;
    mail?: string;
    telefono?: string;
    operatore?: string;
    scade_at?: string;
    stato?: 'green' | 'red' | 'yellow';
  }): Promise<{ message: string; buono: BuonoRow }> => {
    const response = await api.post<{ message: string; buono: BuonoRow }>('/admin/buoni', data);
    return response.data;
  },
  updateBuono: async (
    id: number,
    data: {
      a?: string;
      da?: string;
      regalo?: string;
      mail?: string;
      telefono?: string;
      operatore?: string;
      scade_at?: string;
      stato?: 'green' | 'red' | 'yellow';
    }
  ): Promise<{ message: string; buono: BuonoRow }> => {
    const response = await api.put<{ message: string; buono: BuonoRow }>(`/admin/buoni/${id}`, data);
    return response.data;
  },
  /** URL firmata (valida 7 giorni) per scaricare il PDF del buono (condivisione WhatsApp). */
  getBuonoPdfUrl: async (buonoId: number): Promise<string> => {
    const response = await api.get<{ url: string }>(`/admin/buoni/${buonoId}/pdf-url`);
    return response.data.url;
  },
  /** Carica un PDF temporaneo (generato con html2canvas) e restituisce l'URL firmata per metterla nel messaggio. */
  uploadSharePdf: async (blob: Blob): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('pdf', blob, 'buono-regalo.pdf');
    const response = await api.post<{ url: string }>('/admin/buoni/upload-share-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  /** Invia email dal server con il PDF in allegato (SMTP obbligatorio). pdf = blob da html2canvas, to = email, buonoId = id buono. */
  sendShareEmailWithPdf: async (blob: Blob, to: string, buonoId: number): Promise<{ message: string }> => {
    const formData = new FormData();
    formData.append('pdf', blob, 'buono-regalo.pdf');
    formData.append('to', to);
    formData.append('buono_id', String(buonoId));
    const response = await api.post<{ message: string }>('/admin/buoni/send-share-email-with-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  /** Invia via email il link del buono al destinatario (to opzionale: se assente usa l'email del buono). */
  sendBuonoShareEmail: async (buonoId: number, to?: string | null): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/admin/buoni/${buonoId}/send-share-email`, to ? { to } : {});
    return response.data;
  },
  /** Invia subito l'email via SMTP: con PDF allegato (se buono ha template) o con link. to = email destinatario. */
  sendShareEmailDirect: async (buonoId: number, to: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/admin/buoni/${buonoId}/send-share-email-direct`, { to });
    return response.data;
  },
  deleteBuono: async (id: number): Promise<void> => {
    await api.delete(`/admin/buoni/${id}`);
  },
};

export default api;
